/* eslint-disable camelcase */
import { Module } from 'vuex'
import { RootState } from '../types'
import api from '@/api/axios'

export interface Expense {
  id: number
  property_id: number
  property_name?: string
  title: string
  amount: number
  date: string
  notes?: string
}

export interface ExpensesState {
  list: Expense[]
  loading: boolean
}

const expenses: Module<ExpensesState, RootState> = {
  namespaced: true,

  state: (): ExpensesState => ({
    list: [],
    loading: false
  }),

  getters: {
    all: (state) => state.list,
    loading: (state) => state.loading,
    totalThisMonth: (state) => {
      const now = new Date()
      return state.list
        .filter(e => {
          const d = new Date(e.date)
          return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear()
        })
        .reduce((sum, e) => sum + Number(e.amount), 0)
    }
  },

  mutations: {
    SET_LIST (state, list: Expense[]) { state.list = list },
    SET_LOADING (state, val: boolean) { state.loading = val },
    ADD (state, expense: Expense) { state.list.unshift(expense) },
    UPDATE (state, updated: Expense) {
      const i = state.list.findIndex(e => e.id === updated.id)
      if (i !== -1) state.list.splice(i, 1, updated)
    },
    REMOVE (state, id: number) {
      state.list = state.list.filter(e => e.id !== id)
    }
  },

  actions: {
    async fetchAll ({ commit }, params?: { property_id?: number; month?: string }) {
      commit('SET_LOADING', true)
      try {
        const { data } = await api.get('/expenses', { params })
        commit('SET_LIST', data.data ?? data)
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async create ({ commit }, payload: Partial<Expense>) {
      const { data } = await api.post('/expenses', payload)
      commit('ADD', data.data ?? data)
      return data
    },

    async update ({ commit }, { id, ...payload }: Partial<Expense> & { id: number }) {
      const { data } = await api.put(`/expenses/${id}`, payload)
      commit('UPDATE', data.data ?? data)
      return data
    },

    async remove ({ commit }, id: number) {
      await api.delete(`/expenses/${id}`)
      commit('REMOVE', id)
    }
  }
}

export default expenses
